import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Icon from "../Icon";
import { aiError, useAiAccess, chatMessages, createChat, deleteChat, listChats, rateMessage, sendMessage } from "./aiApi";
import { renderMarkdown } from "./markdown";
import "./ai-chat.css";

/**
 * Chat with the AI Coach (kind "coach", about one course) or the AI Career Advisor ("career").
 * Past chats in a list, the reply streaming in as it's written, thumbs up/down on each reply.
 */
export default function AiChat({ kind, course, name, intro, suggestions = [] }) {
  const access = useAiAccess();
  const [chats, setChats] = useState(null);
  const [left, setLeft] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [failed, setFailed] = useState(false);
  const abort = useRef(null);
  const end = useRef(null);

  const loadChats = useCallback(() => listChats(kind, course)
    .then((r) => { setChats(r.conversations); setLeft(r.messages_left_today); setFailed(false); return r.conversations; })
    .catch(() => { setFailed(true); return []; }), [kind, course]);

  useEffect(() => { if (access) loadChats(); }, [access, loadChats]);
  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => { end.current?.scrollIntoView({ block: "end", behavior: "smooth" }); }, [messages, status]);

  const open = async (chat) => {
    if (busy) return;
    setActive(chat);
    setMessages([]);
    if (!chat) return;
    try { setMessages(await chatMessages(chat.id)); } catch (e) { toast.error(aiError(e, "We couldn't open that chat.")); }
  };

  const send = async (text) => {
    const t = (text ?? draft).trim();
    if (!t || busy) return;
    setBusy(true);
    setDraft("");
    let chat = active;
    try {
      if (!chat) {
        chat = await createChat(kind, course);
        setActive(chat);
      }
    } catch (e) {
      toast.error(aiError(e, "We couldn't start a chat."));
      setBusy(false);
      setDraft(t);
      return;
    }
    setMessages((m) => [...m, { id: `u${Date.now()}`, role: "user", text: t }, { id: "pending", role: "assistant", text: "", pending: true }]);
    const patch = (fn) => setMessages((m) => m.map((x) => (x.id === "pending" ? fn(x) : x)));
    abort.current = new AbortController();
    setStatus("Thinking…");
    try {
      await sendMessage(chat.id, t, {
        text: (d) => { setStatus(""); patch((x) => ({ ...x, text: x.text + d })); },
        tool: (label) => setStatus(`${label}…`),
        done: (messageId) => patch((x) => ({ ...x, id: messageId, pending: false })),
        error: (message) => patch((x) => ({ ...x, id: `e${Date.now()}`, pending: false, error: message, text: x.text })),
      }, abort.current.signal);
    } catch (e) {
      if (e?.name !== "AbortError") patch((x) => ({ ...x, id: `e${Date.now()}`, pending: false, error: "We lost the connection. Try again." }));
    }
    setStatus("");
    setBusy(false);
    loadChats();
  };

  const rate = async (msg, rating) => {
    const next = msg.rating === rating ? 0 : rating;
    setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, rating: next || null } : x)));
    try { await rateMessage(active.id, msg.id, next); } catch { /* not important enough to interrupt */ }
  };

  const remove = async (chat) => {
    try {
      await deleteChat(chat.id);
      if (active?.id === chat.id) { setActive(null); setMessages([]); }
      loadChats();
    } catch (e) { toast.error(aiError(e, "We couldn't delete that chat.")); }
  };

  if (access === null) return <section className="complete-box"><div><strong>Loading {name}…</strong></div></section>;
  if (!access) return <section className="complete-box"><div><strong>{name} isn't switched on yet</strong><small>Check back soon.</small></div></section>;
  if (failed && !chats) return <section className="complete-box"><div><strong>We couldn't reach {name}</strong><small>Check your connection and try again.</small></div><button className="cbtn quiet" type="button" onClick={loadChats}>Try Again</button></section>;

  return (
    <div className="aic">
      <aside className="aic-list" aria-label="Your Chats">
        <button type="button" className="cbtn go aic-new" onClick={() => open(null)} disabled={busy}>+ New chat</button>
        {(chats || []).map((c) => (
          <div key={c.id} className={`aic-item${active?.id === c.id ? " on" : ""}`}>
            <button type="button" onClick={() => open(c)} disabled={busy}>{c.title}</button>
            <button type="button" className="aic-x" aria-label={`Delete "${c.title}"`} onClick={() => remove(c)} disabled={busy}>×</button>
          </div>
        ))}
        {chats && !chats.length && <small className="aic-none">Your chats appear here.</small>}
      </aside>

      <section className="aic-main">
        <div className="aic-scroll" aria-live="polite">
          {!messages.length && (
            <div className="aic-hello">
              <span className="aic-badge"><Icon name={kind === "career" ? "brief" : "chat"} size={20} /></span>
              <strong>{name}</strong>
              <p>{intro}</p>
              {suggestions.length > 0 && (
                <div className="aic-sugg">
                  {suggestions.map((s) => <button key={s} type="button" onClick={() => send(s)} disabled={busy || left === 0}>{s}</button>)}
                </div>
              )}
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`aic-msg ${m.role}`}>
              {m.role === "user" ? <p>{m.text}</p> : (
                <>
                  {m.text && <div className="aic-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }} />}
                  {m.pending && !m.text && <span className="aic-status">{status || "Thinking…"}</span>}
                  {m.pending && m.text && status && <span className="aic-status">{status}</span>}
                  {m.error && <p className="aic-err">{m.error}</p>}
                  {!m.pending && !m.error && (
                    <div className="aic-rate">
                      <button type="button" aria-pressed={m.rating === 1} aria-label="Helpful" onClick={() => rate(m, 1)}>👍</button>
                      <button type="button" aria-pressed={m.rating === -1} aria-label="Not helpful" onClick={() => rate(m, -1)}>👎</button>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
          <div ref={end} />
        </div>

        <form className="aic-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <textarea rows={2} value={draft} maxLength={4000} placeholder={left === 0 ? "You've used today's messages. Back tomorrow." : `Message ${name}…`}
            onChange={(e) => setDraft(e.target.value)} disabled={left === 0}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} aria-label={`Message ${name}`} />
          <button className="cbtn go" type="submit" disabled={busy || !draft.trim() || left === 0}>{busy ? "…" : "Send"}</button>
        </form>
        <small className="aic-foot">{left != null ? `${left} message${left === 1 ? "" : "s"} left today · ` : ""}AI can make mistakes. Check important details with your instructor.</small>
      </section>
    </div>
  );
}
